/**
 * Readable one-liners for `lead_events` rows. Automation events carry a
 * `payload_json.summary` (BUILD_PLAN cross-cutting conventions) which wins
 * over the generic text; stage ids resolve through the stages map.
 */
import { jsonObject, type ActorKind, type LeadEvent, type Stage } from '@/features/pipeline/types';

export const ACTOR_LABEL: Record<ActorKind, string> = {
  user: 'Team member',
  system: 'Automation',
  ai: 'AI assistant',
  integration: 'Integration',
};

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim() : null;
}

/** A note or event that arrived as a text reply (the worker may name the channel in any of these keys). */
function viaSms(payload: Record<string, unknown>): boolean {
  return [payload.via, payload.channel, payload.source, payload.author_kind].some((v) => v === 'sms');
}

function humanize(type: string): string {
  const s = type.replace(/[_-]+/g, ' ').trim();
  return s ? s[0]!.toUpperCase() + s.slice(1) : 'Event';
}

/** Who texted back: the worker names the recipient under one of these (Stage 5 `replies.ts`). */
function replierName(payload: Record<string, unknown>): string {
  const nested = payload.recipient && typeof payload.recipient === 'object' ? (payload.recipient as Record<string, unknown>).name : null;
  return str(payload.recipient_name) ?? str(payload.name) ?? str(nested) ?? 'the team';
}

function channelLabel(payload: Record<string, unknown>): string {
  return payload.channel === 'slack' ? 'Slack post' : 'Text';
}

export function summarizeEvent(event: LeadEvent, stagesById: Map<string, Stage>): string {
  const payload = jsonObject(event.payload_json);
  const summary = typeof payload.summary === 'string' && payload.summary ? payload.summary : null;
  const from = (event.from_stage_id ? stagesById.get(event.from_stage_id)?.name : null) ?? str(payload.from_stage_name);
  const to = (event.to_stage_id ? stagesById.get(event.to_stage_id)?.name : null) ?? str(payload.to_stage_name);

  // Stage 5 QA: a move made by a manager's text reply reads as such, whatever the worker summarised.
  if (event.type === 'stage_changed' && viaSms(payload)) {
    const moved = from && to ? `Moved from ${from} to ${to}` : to ? `Moved to ${to}` : 'Stage changed';
    return `${moved} — text reply from ${replierName(payload)}`;
  }
  // Stage 6: a failed delivery always shows why, even when the summary left the error out.
  if (event.type === 'message_failed') {
    const error = str(payload.error);
    if (summary) return error && !summary.includes(error) ? `${summary} — ${error}` : summary;
    const kind = str(payload.kind) ? ` (${str(payload.kind)})` : '';
    const dest = str(payload.to) ? ` to ${str(payload.to)}` : '';
    return `${channelLabel(payload)}${kind}${dest} failed${error ? `: ${error}` : ''}`;
  }
  if (summary) return summary;

  const reason = typeof payload.reason === 'string' && payload.reason ? ` — ${payload.reason}` : '';

  switch (event.type) {
    case 'created': {
      const source = typeof payload.source === 'string' ? payload.source : null;
      return source ? `Applied via ${source}` : 'Application received';
    }
    case 'reapplied':
      return 'Applied again — answers merged into this card';
    case 'stage_changed':
      if (from && to) return `Moved from ${from} to ${to}${reason}`;
      if (to) return `Moved to ${to}${reason}`;
      return `Stage changed${reason}`;
    case 'updated': {
      const fields = Array.isArray(payload.fields)
        ? payload.fields.filter((f): f is string => typeof f === 'string')
        : Object.keys(payload).filter((k) => k !== 'summary');
      return fields.length ? `Updated ${fields.map((f) => f.replace(/_/g, ' ')).join(', ')}` : 'Details updated';
    }
    case 'note_added': {
      if (!viaSms(payload)) return 'Note added';
      const by = str(payload.author_name) ?? str(payload.recipient_name);
      return by ? `Note added by text from ${by}` : 'Note added by text';
    }
    case 'nudge_sent': {
      const to = str(payload.recipient_name);
      const count = typeof payload.recipient_count === 'number' ? payload.recipient_count : Array.isArray(payload.recipients) ? payload.recipients.length : null;
      if (to) return `Nudge texted to ${to} — “Have you spoken to this candidate yet?”`;
      if (count && count > 1) return `Nudge texted to ${count} recipients — “Have you spoken to this candidate yet?”`;
      return 'Nudge texted to the team — “Have you spoken to this candidate yet?”';
    }
    case 'not_contacted': {
      const by = str(payload.recipient_name);
      return `${by ? `${by} replied` : 'Reply'} 4 — not contacted yet. The team will be asked again after the cooldown.`;
    }
    case 'message_sent': {
      const dest = str(payload.to) ? ` to ${str(payload.to)}` : '';
      return `${payload.dry_run === true ? 'Would send' : 'Sent'} ${channelLabel(payload).toLowerCase()}${dest}`;
    }
    case 'assessment_sent':
      return 'Assessment link texted to the candidate';
    case 'assessment_completed':
      return 'Candidate completed the assessment';
    case 'automation_skipped':
      return `Automation skipped${reason}`;
    default:
      return humanize(event.type) + (to ? ` → ${to}` : '');
  }
}
